import { useEffect, useMemo } from 'react';

import { TFunction } from 'i18next';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';

import { Currency } from '@/modules/common';
import { ProjectConflictResolutionContext } from '@/modules/projects/types/project-conflict-resolution-context';
import {
  ConflictFieldConfig,
  ConflictFieldGroup,
  ConflictFieldRow,
  ConflictSnapshot,
  SubRowData,
} from '@/modules/projects/types/project-conflicts';
import { areValuesEqual } from '@/utils/form-state.utils';

export interface UseConflictResolutionParams<TFieldKey extends string> {
  snapshots: ConflictSnapshot[];
  currentFormValues: Record<TFieldKey, unknown>;
  fields: ConflictFieldConfig<TFieldKey>[];
  groupedFields?: ConflictFieldGroup<TFieldKey>[];
  t: TFunction;
  translationNamespace: string;
  context?: ProjectConflictResolutionContext;
}

export interface UseConflictResolutionReturn {
  tableData: ConflictFieldRow[];
  groupedData?: Array<{ category: string; items: ConflictFieldRow[] }>;
  form: UseFormReturn<Record<string, string>>;
  getResolvedValues: () => Record<string, unknown>;
  dynamicContext: ProjectConflictResolutionContext;
}

/**
 * Generic hook for processing conflict resolution data
 * Transforms snapshots and field configurations into table-ready format
 *
 * Filtering rules:
 * 1. Fields with alwaysExcluded=true are never shown
 * 2. Fields are only shown if values differ between form and any snapshot
 */
export const useConflictResolution = <TFieldKey extends string>({
  snapshots,
  currentFormValues,
  fields,
  groupedFields,
  t,
  translationNamespace,
  context,
}: UseConflictResolutionParams<TFieldKey>): UseConflictResolutionReturn => {
  /**
   * Gets the snapshot key for a field (uses snapshotKey if provided, otherwise uses key)
   */
  const getSnapshotKey = (field: ConflictFieldConfig<TFieldKey>): string => {
    return field.snapshotKey || field.key;
  };

  /**
   * Gets the comparable form value for a field
   */
  const getComparableFormValue = (field: ConflictFieldConfig<TFieldKey>): any => {
    const formValue = currentFormValues[field.key];
    if (field.getFormValueForComparison) {
      return field.getFormValueForComparison(formValue);
    }
    return formValue;
  };

  /**
   * Normalizes a raw snapshot value using the field's normalizeSnapshotValue transformer.
   * Applied before display and comparison so both receive the same normalized value.
   */
  const getNormalizedSnapshotValue = (
    field: ConflictFieldConfig<TFieldKey>,
    rawValue: any
  ): any => {
    return field.normalizeSnapshotValue ? field.normalizeSnapshotValue(rawValue) : rawValue;
  };

  /**
   * Gets the comparable snapshot value for a field.
   * Applies normalizeSnapshotValue first, then getSnapshotValueForComparison if defined.
   */
  const getComparableSnapshotValue = (
    field: ConflictFieldConfig<TFieldKey>,
    snapshotValue: any
  ): any => {
    const normalized = getNormalizedSnapshotValue(field, snapshotValue);
    if (field.getSnapshotValueForComparison) {
      return field.getSnapshotValueForComparison(normalized);
    }
    return normalized;
  };

  /**
   * Checks if a field has any differences between form value and snapshot values
   */
  const hasFieldDifferences = (field: ConflictFieldConfig<TFieldKey>): boolean => {
    const snapshotKey = getSnapshotKey(field);
    const comparableFormValue = getComparableFormValue(field);

    // Check if form value differs from any snapshot value
    for (const snapshot of snapshots) {
      const rawSnapshotValue = snapshot.data[snapshotKey];
      const comparableSnapshotValue = getComparableSnapshotValue(field, rawSnapshotValue);

      if (!areValuesEqual(comparableFormValue, comparableSnapshotValue)) {
        return true;
      }
    }

    // Also check if snapshots differ from each other
    for (let i = 0; i < snapshots.length; i++) {
      for (let j = i + 1; j < snapshots.length; j++) {
        const valueI = getComparableSnapshotValue(field, snapshots[i].data[snapshotKey]);
        const valueJ = getComparableSnapshotValue(field, snapshots[j].data[snapshotKey]);
        if (!areValuesEqual(valueI, valueJ)) {
          return true;
        }
      }
    }

    return false;
  };

  /**
   * Filters fields to only include those that should be shown in the table
   */
  const filteredFields = useMemo(() => {
    // First pass: determine which fields have actual differences
    const fieldsWithDiffs = new Set(
      fields
        .filter(field => !field.alwaysExcluded && hasFieldDifferences(field))
        .map(field => field.key)
    );

    return fields.filter(field => {
      // Rule 1: Exclude fields marked as alwaysExcluded
      if (field.alwaysExcluded) {
        return false;
      }

      // Rule 2: Include fields with differences
      if (fieldsWithDiffs.has(field.key)) {
        return true;
      }

      // Rule 3: Include fields that are linked to another field that has differences
      if (field.showAlongsideField && fieldsWithDiffs.has(field.showAlongsideField)) {
        return true;
      }

      return false;
    });
  }, [fields, snapshots, currentFormValues]);

  /**
   * Computes initial form values including sub-rows
   * Applies normalizeFormValue if the field provides one
   */
  const computeInitialValues = useMemo(() => {
    const values: Record<string, string> = {};

    filteredFields.forEach(field => {
      if (field.hasSubRows && field.getSubRows) {
        // For fields with sub-rows, create form values for each sub-row
        const formValue = currentFormValues[field.key];
        const subRows = field.getSubRows(formValue, snapshots);
        subRows.forEach((row: SubRowData) => {
          const rowKey = `${field.key}_${row.id}`;
          const normalizedValue = field.normalizeFormValue
            ? field.normalizeFormValue(row.currentValue)
            : row.currentValue;
          values[rowKey] = JSON.stringify(normalizedValue);
        });
      } else {
        // Regular field
        const currentValue = currentFormValues[field.key];
        const normalizedValue = field.normalizeFormValue
          ? field.normalizeFormValue(currentValue)
          : currentValue;
        values[field.key] = JSON.stringify(normalizedValue);
      }
    });

    return values;
  }, [filteredFields, currentFormValues, snapshots]);

  const form = useForm({
    defaultValues: computeInitialValues,
  });

  // Watch all form fields for changes to trigger re-renders
  const watchedFormValues = useWatch({ control: form.control });

  // Reset form when currentFormValues or filteredFields change
  useEffect(() => {
    form.reset(computeInitialValues);
  }, [computeInitialValues, form]);

  /**
   * Gets current selected values from form for conditional visibility.
   * Seeded with currentFormValues so that shouldShow/isDisabled checks work correctly
   * even for fields that have no differences (and are therefore not in filteredFields).
   */
  const getSelectedValues = (): Record<string, any> => {
    const formValues = form.getValues();
    // Start with all current form values as fallback for fields not in the conflict form
    const selectedValues: Record<string, any> = { ...currentFormValues };

    filteredFields.forEach(field => {
      const jsonValue = formValues[field.key];
      try {
        selectedValues[field.key] = jsonValue
          ? JSON.parse(jsonValue)
          : currentFormValues[field.key];
      } catch {
        selectedValues[field.key] = currentFormValues[field.key];
      }
    });

    return selectedValues;
  };

  /**
   * Computes dynamic context based on selected form values
   * Merges base context with dynamically selected values (e.g., currency, rateSource)
   */
  const dynamicContext = useMemo(() => {
    const selectedValues = getSelectedValues();

    // Start with base context
    const mergedContext = { ...context };

    // If defaultCurrency field exists and has a selected value, update currency in context
    // Also handles internalRateCurrency for team member billing conflicts
    const selectedCurrencyValue =
      selectedValues.defaultCurrency ??
      selectedValues.currency ??
      selectedValues.internalRateCurrency;
    if (selectedCurrencyValue) {
      // Extract currency code from object or use string directly
      const currencyValue = selectedCurrencyValue;
      let currencyCode: string | undefined;

      if (typeof currencyValue === 'string') {
        currencyCode = currencyValue;
      } else if (
        typeof currencyValue === 'object' &&
        currencyValue !== null &&
        'code' in currencyValue
      ) {
        currencyCode = String(currencyValue.code);
      }

      if (currencyCode) {
        // Try to look up the full Currency object from the currencies list (if provided in context)
        const currenciesList = context?.currencies;
        const fullCurrency = Array.isArray(currenciesList)
          ? (currenciesList as Currency[]).find(c => c.code === currencyCode)
          : undefined;
        mergedContext.currency = fullCurrency ?? new Currency(currencyCode, '');
      }
    }

    // If rateSource field exists and has a selected value, update rateSource in context
    if (selectedValues.rateSource) {
      mergedContext.rateSource = selectedValues.rateSource;
    }

    return mergedContext;
  }, [context, watchedFormValues]);

  /**
   * Creates a single table row for a field
   */
  const createTableRow = (field: ConflictFieldConfig<TFieldKey>): ConflictFieldRow => {
    const snapshotValues = new Map<string, any>();
    const snapshotKey = getSnapshotKey(field);

    // Extract values from each snapshot using the correct snapshotKey.
    // Apply normalizeSnapshotValue so display and comparison use the same scale.
    snapshots.forEach(snapshot => {
      const rawValue = snapshot.data[snapshotKey];
      snapshotValues.set(snapshot.id, getNormalizedSnapshotValue(field, rawValue));
    });

    const selectedValueJson = form.watch(field.key as string);
    let selectedValue;
    try {
      selectedValue = selectedValueJson
        ? JSON.parse(selectedValueJson)
        : currentFormValues[field.key];
    } catch {
      selectedValue = currentFormValues[field.key];
    }

    return {
      fieldKey: field.key,
      fieldLabel: t(`${translationNamespace}:${field.labelKey}`),
      currentValue: currentFormValues[field.key],
      snapshotValues,
      selectedValue,
      fieldConfig: field,
    };
  };

  /**
   * Checks if a sub-row has any differences between current value and snapshot values
   */
  const hasSubRowDifferences = (subRow: SubRowData): boolean => {
    const currentValue = subRow.currentValue;
    const snapshotValuesArray = Array.from(subRow.snapshotValues.values());

    // Check if current value differs from any snapshot value
    for (const snapshotValue of snapshotValuesArray) {
      if (!areValuesEqual(currentValue, snapshotValue)) {
        return true;
      }
    }

    // Check if snapshot values differ from each other
    for (let i = 0; i < snapshotValuesArray.length; i++) {
      for (let j = i + 1; j < snapshotValuesArray.length; j++) {
        if (!areValuesEqual(snapshotValuesArray[i], snapshotValuesArray[j])) {
          return true;
        }
      }
    }

    return false;
  };

  /**
   * Creates sub-rows for a field with hasSubRows: true
   * Filters out sub-rows with no differences
   */
  const createSubRows = (field: ConflictFieldConfig<TFieldKey>): ConflictFieldRow[] => {
    if (!field.getSubRows) {
      return [];
    }

    const formValue = currentFormValues[field.key];
    const subRowsData = field.getSubRows(formValue, snapshots);

    // Filter sub-rows to only include those with differences
    const filteredSubRows = subRowsData.filter(hasSubRowDifferences);

    return filteredSubRows.map((subRow: SubRowData) => {
      const rowKey = `${field.key}_${subRow.id}`;
      const selectedValueJson = form.watch(rowKey);
      let selectedValue;
      try {
        selectedValue = selectedValueJson ? JSON.parse(selectedValueJson) : subRow.currentValue;
      } catch {
        selectedValue = subRow.currentValue;
      }

      return {
        fieldKey: rowKey,
        fieldLabel: subRow.label,
        currentValue: subRow.currentValue,
        snapshotValues: subRow.snapshotValues,
        selectedValue,
        isSubRow: true,
        parentFieldKey: field.key,
        subRowData: subRow,
        fieldConfig: field,
      };
    });
  };

  /**
   * Creates table rows for filtered fields only
   * Handles both regular fields and fields with sub-rows
   * Applies conditional visibility based on shouldShow function
   */
  const tableData: ConflictFieldRow[] = useMemo(() => {
    const rows: ConflictFieldRow[] = [];
    const selectedValues = getSelectedValues();

    filteredFields.forEach(field => {
      // Check if field should be visible based on shouldShow function
      if (field.shouldShow && !field.shouldShow(selectedValues)) {
        return; // Skip this field
      }

      if (field.hasSubRows && field.getSubRows) {
        // Create multiple sub-rows for array fields
        rows.push(...createSubRows(field));
      } else {
        // Create single row for regular fields
        rows.push(createTableRow(field));
      }
    });

    return rows;
  }, [filteredFields, snapshots, currentFormValues, form, watchedFormValues]);

  /**
   * Groups table rows if groupedFields is provided
   * Handles both regular fields and sub-rows (matched by parentFieldKey)
   * Filters out groups with no items to avoid showing empty headers
   */
  const groupedData = groupedFields
    ? groupedFields
        .map(group => ({
          category: group.headerLabelKey,
          items: tableData.filter(row => {
            // Regular field match
            if (group.fields.includes(row.fieldKey as TFieldKey)) {
              return true;
            }
            // Sub-row match - check if parent field is in the group
            if (row.isSubRow && row.parentFieldKey) {
              return group.fields.includes(row.parentFieldKey as TFieldKey);
            }
            return false;
          }),
        }))
        .filter(group => group.items.length > 0) // Only include groups with items
    : undefined;

  /**
   * Returns the final resolved values selected by the user
   * Parses JSON strings back to actual values and applies normalizeFormValue
   * For sub-rows, reconstructs the array with selected values
   * Excludes hidden fields based on shouldShow function
   */
  const getResolvedValues = () => {
    const formValues = form.getValues();
    const resolved: Record<string, any> = {};
    const selectedValues = getSelectedValues();

    filteredFields.forEach(field => {
      // Skip hidden fields - don't include them in resolved values
      if (field.shouldShow && !field.shouldShow(selectedValues)) {
        return;
      }

      if (field.hasSubRows && field.getSubRows) {
        // For fields with sub-rows, collect values from all sub-rows
        const formValue = currentFormValues[field.key];
        const subRows = field.getSubRows(formValue, snapshots);

        // Build array of resolved values for each item
        const resolvedItems: any[] = [];
        subRows.forEach((row: SubRowData) => {
          const rowKey = `${field.key}_${row.id}`;
          const jsonValue = formValues[rowKey];
          try {
            let value = jsonValue ? JSON.parse(jsonValue) : row.currentValue;
            // Apply normalizeFormValue if provided
            if (field.normalizeFormValue && value !== null && value !== undefined) {
              value = field.normalizeFormValue(value);
            }
            // Always include the entry so consumers can distinguish "explicitly set to null"
            // (user selected "-") from "not in the resolved map" (member not in conflict table).
            resolvedItems.push({ id: row.id, value: value ?? null });
          } catch {
            let currentValue = row.currentValue;
            // Apply normalizeFormValue if provided
            if (field.normalizeFormValue && currentValue !== null && currentValue !== undefined) {
              currentValue = field.normalizeFormValue(currentValue);
            }
            resolvedItems.push({ id: row.id, value: currentValue ?? null });
          }
        });

        resolved[field.key] = resolvedItems;
      } else {
        // Regular field
        const jsonValue = formValues[field.key];
        try {
          let value = jsonValue ? JSON.parse(jsonValue) : currentFormValues[field.key];
          // Apply normalizeFormValue if provided
          if (field.normalizeFormValue && value !== null && value !== undefined) {
            value = field.normalizeFormValue(value);
          }
          resolved[field.key] = value;
        } catch {
          let value = currentFormValues[field.key];
          // Apply normalizeFormValue if provided
          if (field.normalizeFormValue && value !== null && value !== undefined) {
            value = field.normalizeFormValue(value);
          }
          resolved[field.key] = value;
        }
      }
    });

    return resolved;
  };

  return {
    tableData,
    groupedData,
    form,
    getResolvedValues,
    dynamicContext,
  };
};
